// Placeholder content for form previews: keeps a brand-new form's preview
// looking like a real page until the merchant fills in real values.
// HostedPagePreviewFrame applies these at the postMessage boundary, so the
// form-builder state itself is never polluted with dummy values.

/**
 * The previewData payload streamed to the hosted-page preview iframe. The
 * full shape is owned by the previewers (45+ fields, mirrored by Base.jsx's
 * preview mapping); only the fields the placeholder/portability pipeline
 * touches are typed explicitly.
 */
export interface HostedPreviewData {
  formName?: string;
  formType?: string;
  convertedContent?: string;
  image?: string | null;
  enableDonorList?: boolean;
  donorListData?: unknown;
  [key: string]: unknown;
}

interface PreviewDonor {
  name: string;
  grossAmount: number;
  timestamp: string;
  frequency: string;
  city: string;
  country: string;
  anonymous: boolean;
  automatedDonation: boolean;
  formName: string;
}

export const DUMMY_DONOR_LIST_PREVIEW = (() => {
  const now = Date.now();
  const donors: PreviewDonor[] = [
    {
      name: 'John Smith',
      grossAmount: 250,
      timestamp: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
      frequency: 'MONTHLY',
      city: 'New York',
      country: 'United States',
      anonymous: false,
      automatedDonation: false,
      formName: '',
    },
    {
      name: 'Jane Doe',
      grossAmount: 150,
      timestamp: new Date(now - 5 * 60 * 60 * 1000).toISOString(),
      frequency: '',
      city: 'Los Angeles',
      country: 'United States',
      anonymous: false,
      automatedDonation: false,
      formName: '',
    },
    {
      name: 'Michael Johnson',
      grossAmount: 500,
      timestamp: new Date(now - 24 * 60 * 60 * 1000).toISOString(),
      frequency: 'MONTHLY',
      city: 'Chicago',
      country: 'United States',
      anonymous: false,
      automatedDonation: false,
      formName: '',
    },
    {
      name: 'Sarah Williams',
      grossAmount: 100,
      timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
      frequency: 'WEEKLY',
      city: 'Houston',
      country: 'United States',
      anonymous: false,
      automatedDonation: false,
      formName: '',
    },
  ];
  const topDonors = [...donors].sort((a, b) => b.grossAmount - a.grossAmount);
  return { recentDonors: donors, topDonors };
})();

const PLACEHOLDER_DESCRIPTION =
  '<p>Welcome to your campaign page! Use the description to share your story — what you are raising money for, why it matters, and what your supporters make possible.</p>';

const stripHtml = (html: unknown): string =>
  String(html ?? '')
    .replace(/<[^>]*>/g, '')
    .trim();

export function withPreviewPlaceholders(
  previewData: HostedPreviewData | null | undefined,
): HostedPreviewData | null | undefined {
  if (!previewData) return previewData;
  const next: HostedPreviewData = { ...previewData };
  if (!String(next.formName ?? '').trim()) {
    next.formName = next.formType === 'EVENT' ? 'Enter Event Title' : 'Enter Form Title';
  }
  // Rich-text editors emit empty markup like <p></p>; treat that as empty too.
  if (!stripHtml(next.convertedContent)) {
    next.convertedContent = PLACEHOLDER_DESCRIPTION;
  }
  if (next.enableDonorList && !next.donorListData) {
    next.donorListData = DUMMY_DONOR_LIST_PREVIEW;
  }
  return next;
}
